// Click/tap-to-set polygon editor. Pointer events on the overlay canvas
// append vertices (in normalized 0-1 frame coords) until the user clicks
// "Clear zone". The underlying frame size is provided by main so the same
// polygon scales when the camera resolution changes.

import type { PolygonPoint } from "../inference/types";

export interface PolygonEditorOptions {
  canvas: HTMLCanvasElement;
  initial?: PolygonPoint[];
  onChange: (polygon: PolygonPoint[]) => void;
}

// A "tap" is a touch that didn't move further than this many CSS pixels —
// anything more is treated as a drag and ignored (so future drag handles or
// page scrolls don't accidentally place vertices).
const TAP_MOVEMENT_THRESHOLD_PX = 8;

export class PolygonEditor {
  polygon: PolygonPoint[];
  private canvas: HTMLCanvasElement;
  private onChange: (polygon: PolygonPoint[]) => void;
  /** Frame width/height the polygon will be evaluated against (pixel coords). */
  private frameSize: { w: number; h: number } = { w: 1, h: 1 };
  private touchStart: { x: number; y: number } | null = null;

  constructor(opts: PolygonEditorOptions) {
    this.canvas = opts.canvas;
    this.polygon = opts.initial ? [...opts.initial] : [];
    this.onChange = opts.onChange;
    this.canvas.addEventListener("click", this.handleClick);
    // iOS Safari sometimes fails to synthesize a click event from a tap on
    // a <canvas>. Bind touch events directly so taps are reliable on phones.
    this.canvas.addEventListener("touchstart", this.handleTouchStart, { passive: true });
    this.canvas.addEventListener("touchend", this.handleTouchEnd, { passive: false });
  }

  setFrameSize(size: { w: number; h: number }): void {
    this.frameSize = size;
  }

  setPolygon(polygon: PolygonPoint[]): void {
    this.polygon = [...polygon];
    this.onChange(this.polygon);
  }

  clear(): void {
    this.polygon = [];
    this.onChange(this.polygon);
  }

  private addVertex(clientX: number, clientY: number): void {
    if (this.frameSize.w <= 0 || this.frameSize.h <= 0) return;
    const rect = this.canvas.getBoundingClientRect();
    const xCss = (clientX - rect.left) / rect.width;
    const yCss = (clientY - rect.top) / rect.height;
    if (xCss < 0 || xCss > 1 || yCss < 0 || yCss > 1) return;
    this.polygon.push({ x: clamp(xCss, 0, 1), y: clamp(yCss, 0, 1) });
    this.onChange(this.polygon);
  }

  private handleClick = (e: MouseEvent): void => {
    this.addVertex(e.clientX, e.clientY);
  };

  private handleTouchStart = (e: TouchEvent): void => {
    const t = e.changedTouches[0];
    if (!t) return;
    this.touchStart = { x: t.clientX, y: t.clientY };
  };

  private handleTouchEnd = (e: TouchEvent): void => {
    const t = e.changedTouches[0];
    const start = this.touchStart;
    this.touchStart = null;
    if (!t || !start) return;
    // Only treat brief taps as vertex placements — drags should not place.
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.hypot(dx, dy) > TAP_MOVEMENT_THRESHOLD_PX) return;
    // Suppress the synthetic click that follows touchend so we don't double-add.
    e.preventDefault();
    this.addVertex(t.clientX, t.clientY);
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
